(() => {
  const el = document.querySelector('script[data-petparty-khaki-gift]');
  if (!el || window.__petpartyKhakiGiftInit) return;
  window.__petpartyKhakiGiftInit = true;

  const triggerIds = new Set((el.dataset.triggerVariantIds || '').split(',').map(Number).filter(Boolean));
  let snacks = [];
  try { snacks = JSON.parse(el.dataset.snacks || '[]'); } catch (_) {}
  snacks = snacks.filter(s => Number(s.id) && s.available && Number(s.inventory) > 0);

  const GIFT_PROP = '_petparty_free_khaki_snack';
  const GIFT_VALUE = 'true';
  let reconciling = false;
  let queued = false;
  let timer = null;
  let CartLinesUpdateEvent = null;

  import('@shopify/events').then(mod => { CartLinesUpdateEvent = mod.CartLinesUpdateEvent; }).catch(() => {});

  async function notifyThemeCartChanged(source) {
    if (!CartLinesUpdateEvent) return;
    try {
      const cart = await getCart();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: Promise.resolve({
          cart,
          detail: { sections: null, items: cart.items, itemCount: cart.item_count, source, didError: false }
        })
      }));
    } catch (_) {}
  }

  const getCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    .then(r => { if (!r.ok) throw new Error('cart fetch failed'); return r.json(); });

  function desiredGiftQty(cart) {
    return cart.items.reduce((sum, item) => {
      if (!triggerIds.has(Number(item.variant_id))) return sum;
      // Shopify fixed-bundle child/component lines must never qualify independently.
      if (item.parent_relationship && item.parent_relationship.parent) return sum;
      return sum + Number(item.quantity || 0);
    }, 0);
  }

  function giftLines(cart) {
    return cart.items.filter(item => item.properties && String(item.properties[GIFT_PROP]) === GIFT_VALUE);
  }

  function sortCandidates(list) {
    return [...list].sort((a, b) =>
      Number(b.inventory) - Number(a.inventory) ||
      String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' }) ||
      String(a.sku).localeCompare(String(b.sku), undefined, { numeric: true })
    );
  }

  async function removeExcess(lines, excess) {
    const updates = {};
    for (let i = lines.length - 1; i >= 0 && excess > 0; i--) {
      const line = lines[i];
      const take = Math.min(excess, Number(line.quantity || 0));
      updates[line.key] = Number(line.quantity || 0) - take;
      excess -= take;
    }
    if (!Object.keys(updates).length) return;
    const r = await fetch('/cart/update.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!r.ok) throw new Error('gift removal failed');
    await notifyThemeCartChanged('petparty-khaki-gift-remove');
  }

  async function addMissing(missing, cart) {
    if (missing <= 0) return;
    const existingByVariant = new Map();
    for (const item of cart.items) {
      existingByVariant.set(Number(item.variant_id), (existingByVariant.get(Number(item.variant_id)) || 0) + Number(item.quantity || 0));
    }

    // Allocate sequentially by effective remaining inventory. This keeps the "highest stock"
    // rule true when more than one gift is required in the same reconciliation.
    const pool = sortCandidates(snacks).map(s => ({
      ...s,
      remaining: Math.max(0, Number(s.inventory) - (existingByVariant.get(Number(s.id)) || 0))
    }));
    const allocations = new Map();

    for (let n = 0; n < missing; n++) {
      pool.sort((a, b) =>
        b.remaining - a.remaining ||
        String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' }) ||
        String(a.sku).localeCompare(String(b.sku), undefined, { numeric: true })
      );
      const chosen = pool.find(s => s.remaining > 0);
      if (!chosen) break;
      allocations.set(chosen.id, (allocations.get(chosen.id) || 0) + 1);
      chosen.remaining -= 1;
    }

    for (const [id, quantity] of allocations) {
      const r = await fetch('/cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity, properties: { [GIFT_PROP]: GIFT_VALUE } }] })
      });
      if (!r.ok) {
        // Stock can change after page render. Skip this candidate rather than blocking cart usage.
        console.warn('Pet Party gift: snack kon niet worden toegevoegd; voorraad kan gewijzigd zijn.');
      }
    }
    await notifyThemeCartChanged('petparty-khaki-gift-add');
  }

  async function reconcile() {
    if (window.__graveringMutationLock) {
      clearTimeout(timer);
      timer = setTimeout(reconcile, 180);
      return;
    }
    if (reconciling) { queued = true; return; }
    reconciling = true;
    try {
      const cart = await getCart();
      const desired = desiredGiftQty(cart);
      const gifts = giftLines(cart);
      const current = gifts.reduce((sum, x) => sum + Number(x.quantity || 0), 0);

      if (current > desired) {
        await removeExcess(gifts, current - desired);
      } else if (current < desired) {
        await addMissing(desired - current, cart);
      }
    } catch (err) {
      console.error('Pet Party khaki gift:', err);
    } finally {
      reconciling = false;
      if (queued) { queued = false; setTimeout(reconcile, 80); }
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 120);
  }

  // Initial cart, native cart events, and a fetch observer cover product forms,
  // cart drawer changes and cart-page quantity/removal actions without replacing Vessel handlers.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();

  document.addEventListener('cart:update', schedule);
  document.addEventListener('cart:refresh', schedule);
  document.addEventListener('cart:change', schedule);

  const originalFetch = window.fetch;
  window.fetch = async function(input, init) {
    const response = await originalFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      if (url && /\/cart\/(add|change|update)\.js(?:\?|$)/.test(url) && !(init && init.__petpartyGiftInternal)) {
        setTimeout(schedule, 60);
      }
    } catch (_) {}
    return response;
  };
})();